import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { PRIOR_TO_ORDER } from 'constants/condition';
import {
  BrokerConditionStatusEnum,
  SignOffStatusEnum,
  SignOffStatusOptions,
} from 'constants/loanActionEditor';

import {
  getUuid,
  selectIsBrokerOrSubCompanyUser,
  selectIsClaimAllowed,
} from 'store/auth/selectors';
import { selectExtendedUsersValues } from 'store/users/selectors';

import { selectLoanApplication } from './loanApplication.selectors';

const selectLoanConditions = createSelector(
  selectLoanApplication,
  ({ loanConditions }) => loanConditions,
);

const selectLoanConditionsData = createSelector(selectLoanConditions, ({ data }) => data);

// SETTLED (succeeded or failed) — NOT "loaded". Callers can tell "no conditions on this loan"
// apart from "not fetched yet", and a failed fetch still settles so nothing waits on it forever.
export const selectIsLoanConditionsSettled = createSelector(
  selectLoanConditions,
  ({ isSettled }) => !!isSettled,
);

export const selectLoanConditionById = memoizeOne(id =>
  createSelector(selectLoanConditionsData, loanCondition => loanCondition?.[id]),
);

const selectLoanConditionsValues = createSelector(selectLoanConditionsData, Object.values);

export const selectConditionsList = createSelector(
  [
    selectLoanConditionsValues,
    selectIsBrokerOrSubCompanyUser,
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_CONDITIONS_MANUALLY_SIGN_OFF),
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_CONDITIONS_OWN_SIGN_OFF),
    selectExtendedUsersValues,
    getUuid,
  ],
  (loanConditions, isBrokerOrSubCompanyUser, isSignOffAllowed, isOwnSignOffAllowed, users, uuid) =>
    isBrokerOrSubCompanyUser
      ? loanConditions.map(loanCondition => {
          const [status, signOffStatus] = loanCondition.status.type.split('.');

          return {
            ...loanCondition,
            ...loanCondition.lifeTimeInfo,
            createdBy: users.find(user => user.id === loanCondition.lifeTimeInfo.createdByUserId)
              ?.name,
            message: loanCondition.internalText,
            borrowerMessage: loanCondition.borrowerInternalText,
            subCompanyMessage: loanCondition.subCompanyInternalText,
            canBeSignedOff:
              signOffStatus === SignOffStatusEnum.READY &&
              (isSignOffAllowed || isOwnSignOffAllowed) &&
              loanCondition.lifeTimeInfo.createdByUserId === uuid,
            signedOff: status === BrokerConditionStatusEnum.SIGNED_OFF,
            signedOffBy: users.find(user => user.id === loanCondition.status.userId)?.name,
            status: SignOffStatusOptions.find(({ value }) => value === status),
          };
        })
      : loanConditions,
);

const selectGroupedByPriorToLoanConditions = createSelector(selectConditionsList, loanConditions =>
  loanConditions.reduce((acc, loanCondition) => {
    const { priorToType } = loanCondition;

    if (acc[priorToType]) {
      acc[priorToType].push(loanCondition);

      return acc;
    }

    acc[priorToType] = [loanCondition];

    return acc;
  }, {}),
);

export const selectGroupedByPriorToLoanConditionsList = createSelector(
  selectGroupedByPriorToLoanConditions,
  loanConditions =>
    _.sortBy(Object.entries(loanConditions), ([priorTo]) => PRIOR_TO_ORDER[priorTo]),
);

export const selectIsGetConditionsPendingStatus = createSelector(
  selectLoanConditions,
  ({ isGetConditionsPendingStatus }) => isGetConditionsPendingStatus,
);

export const selectIsAddConditionsPendingStatus = createSelector(
  selectLoanConditions,
  ({ isAddConditionsPendingStatus }) => isAddConditionsPendingStatus,
);

export const selectConditionsWithPendingStatus = createSelector(
  selectLoanConditions,
  ({ conditionsWithPendingStatus }) => conditionsWithPendingStatus,
);

// Outstanding = not yet resolved: not signed off, not rejected, not deleted.
// Works for broker (signedOff is mapped by selectConditionsList) and internal
// conditions (signedOff/isDeleted/rejectedDate come raw, as prepareConditionRow relies on).
export const selectOutstandingConditionsCount = createSelector(
  selectConditionsList,
  conditions =>
    conditions.filter(
      ({ isDeleted, rejectedDate, signedOff }) => !isDeleted && !rejectedDate && !signedOff,
    ).length,
);
